import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { memo, useRef, useState } from 'react'

import { useAmplitude } from '@Analytics/hook'
import AddToCart2SVG from '@Assets/SVG/AddToCart2'
import AngleLeftSVG from '@Assets/SVG/AngleLeft'
import AngleRightSVG from '@Assets/SVG/AngleRight'
import CheckRoundedSVG from '@Assets/SVG/CheckRounded'
import { Chip } from '@Components/Chips/Chip/Chip'
import { ImageV3, ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { ProductTileProps } from '@Domains/Product/Tiles/types'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './Tile.module.scss'

export const ProductTile = memo(({
  action,
  alreadyAddedToCart,
  assetURLs = [],
  beforePrice,
  boosted = false,
  bottomChips,
  brandTitle,
  bundleCombinedValue,
  className,
  enableHover = true,
  hoverImage,
  id,
  imageWidth,
  isOutOfStock = false,
  loadingBuyButton,
  disableBuyButton = loadingBuyButton,
  onBuy,
  price,
  primaryImage,
  priority = true,
  sourceID,
  title,
  topChips,
  url,
  variantsColorCount,
}: ProductTileProps) => {
  const { t } = useTranslation()
  const ampli = useAmplitude()
  const [loaded, setLoaded] = useState(false)
  const useAssets = assetURLs.length > 1
  const assetsRef = useRef<HTMLDivElement>(null)
  const [interacted, setInteracted] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)

  const scrollToSlide = (offset: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const container = assetsRef.current
    if (container) {
      container.scrollBy({ behavior: 'smooth', left: offset * container.clientWidth })
    }
  }

  const originalPrice = beforePrice || price
  const overwrittenPrice = beforePrice ? price : undefined
  const showBrand = !!brandTitle
  const showVariants = !!variantsColorCount && variantsColorCount > 1

  return (
    <Link
      analytics={{
        source_id: sourceID,
        dest_page_type: 'product',
        dest_page_type_id: id,
      }}
      className={clsx(
        'th-p',
        className,
        styles.ProductTile,
        priority && styles.priority,
        (!!onBuy || !!action) && styles.withActions,
        !!hoverImage && enableHover && styles.hasHover,
        boosted && 'th-b',
      )}
      href={url}
      id={id}
      onMouseOver={() => setLoaded(true)}
    >
      {isOutOfStock && (
        <div className={styles.outOfStock}>
          <p>{t('cms:OUT_OF_STOCK')}</p>
        </div>
      )}

      {useAssets && (
        <div
          className={clsx(styles.assets, interacted && styles.interacted)}
          onScroll={(event) => {
            setInteracted(true)
            const { clientWidth, scrollLeft } = event.currentTarget
            if (!clientWidth) {
              return
            }
            const slide = Math.round(scrollLeft / clientWidth)
            if (slide !== activeSlide) {
              // mirrors the product page gallery event; fires once per slide
              // change regardless of input (swipe, trackpad or arrows)
              ampli.galleryInteracted({
                asset_position: slide + 1,
                asset_type: 'image',
                source_id: sourceID,
                type: slide > activeSlide ? 'next' : 'previous',
              })
              setActiveSlide(slide)
            }
          }}
          ref={assetsRef}
        >
          <div className={styles.slide}>
            <ImageV3
              alt={title}
              className={styles.image}
              decoding="async"
              draggable={false}
              fetchPriority={priority ? 'high' : undefined}
              fill
              loading={priority ? 'eager' : 'lazy'}
              onLoad={() => setLoaded(true)}
              priority={priority}
              sizes="(max-width: 767px) 50vw, (max-width: 1199px) 33vw, (max-width: 1999px) 25vw, 20vw"
              src={assetURLs[0]}
              style={{
                objectFit: 'contain',
              }}
              title={title}
            />
          </div>

          {assetURLs.slice(1).map((asset, index) => (
            <div key={asset} className={styles.slide}>
              {/* the second slide always renders (the scroll hint reveals it);
                  the rest only load once the slider has been interacted with —
                  browsers prefetch lazy images in scrollers too eagerly to
                  rely on loading="lazy" alone.
                  no .image class: fill's position: absolute must survive so the
                  image escapes the slide padding and renders to the edge.
                  alt is empty on purpose: inside the tile link these are
                  decorative repeats of the product the link already names */}
              {(index === 0 || interacted) && (
                <ImageV3
                  alt=""
                  decoding="async"
                  draggable={false}
                  fetchPriority="low"
                  fill
                  loading="lazy"
                  overrideQuality="50,65"
                  sizes="(max-width: 767px) 50vw, (max-width: 1199px) 33vw, (max-width: 1999px) 25vw, 20vw"
                  src={asset}
                  style={{
                    objectFit: 'cover',
                  }}

                />
              )}
            </div>
          ))}
        </div>
      )}

      {useAssets && (
        <div className={styles.dots}>
          {assetURLs.map((asset, index) => (
            <span key={asset} className={clsx(styles.dot, index === activeSlide && styles.activeDot)} />
          ))}
        </div>
      )}

      {useAssets && (
        <button
          aria-label="Previous"
          className={clsx(styles.sliderArrow, styles.left)}
          disabled={activeSlide === 0}
          onClick={scrollToSlide(-1)}
          tabIndex={-1}
          type="button"
        >
          <AngleLeftSVG />
        </button>
      )}

      {useAssets && (
        <button
          aria-label="Next"
          className={clsx(styles.sliderArrow, styles.right)}
          disabled={activeSlide >= assetURLs.length - 1}
          onClick={scrollToSlide(1)}
          tabIndex={-1}
          type="button"
        >
          <AngleRightSVG />
        </button>
      )}

      {!useAssets && (
        <>
          {!priority && primaryImage && (
            <ImageV4
              alt={title}
              className={clsx(styles.images, styles.image)}
              decoding="async"
              draggable={false}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              src={primaryImage}
              style={{
                objectFit: 'contain',
              }}
              title={title}
              width={imageWidth || (priority || enableHover) ? 400 : 180}
            />
          )}

          {priority && primaryImage && (
            <ImageV3
              alt={title}
              className={clsx(styles.images, styles.image)}
              decoding="async"
              draggable={false}
              fetchPriority="high"
              fill
              loading="eager"
              onLoad={() => setLoaded(true)}
              priority
              sizes="(max-width: 767px) 50vw, (max-width: 1199px) 33vw, (max-width: 1999px) 25vw, 20vw"
              src={primaryImage}
              style={{
                objectFit: 'contain',
              }}
              title={title}
            />
          )}

          {hoverImage && loaded && enableHover && (
            <ImageV4
              alt={`hover: ${title}`}
              className={clsx(styles.images, styles.imageHover)}
              decoding="async"
              draggable={false}
              loading="lazy"
              src={hoverImage}
              style={{
                objectFit: 'cover',
              }}
              title={`hover: ${title}`}
              width={imageWidth || enableHover ? 500 : 180}
            />
          )}
        </>
      )}

      <p className={styles.title}>{title}</p>

      {!isEmpty(topChips) && (
        <div className={styles.topChips}>
          {map(topChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}

      {!isEmpty(bottomChips) && (
        <div className={styles.bottomChips}>
          {map(bottomChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}

      <div className={styles.price}>
        <p className={clsx(overwrittenPrice && styles.strikeThrough)}>{originalPrice}</p>
        <p>{overwrittenPrice}</p>
      </div>

      {bundleCombinedValue && (
        <div className={styles.bundle}>
          {`${t('common:BUNDLE_COMBINED_VALUE')} - ${bundleCombinedValue}`}
        </div>
      )}

      {action && (
        <div className={styles.actions}>
          {action}
        </div>
      )}

      {!!onBuy && (
        <div className={styles.actions}>
          <button
            className={clsx(styles.buyButton, alreadyAddedToCart && styles.success, sourceID)}
            disabled={disableBuyButton}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onBuy(id)
            }}
            type="button"
          >
            {' '}
            {loadingBuyButton && <Spinner className={styles.addToCartSpinner} />}
            {(!loadingBuyButton && !alreadyAddedToCart) && <AddToCart2SVG />}
            {(!loadingBuyButton && alreadyAddedToCart) && <CheckRoundedSVG className={styles.alreadyAddedIcon} />}
          </button>
        </div>
      )}

      <AddToWishList
        className={styles.wish}
        id={id}
        sourceID={sourceID}
      />

      {!!(showBrand || showVariants) && (
        <div className={styles.brandVariants}>
          {showVariants && (
            <span>
              <p className={styles.variants}>{`${variantsColorCount} ${t('products:COLOURS_LABEL')}`}</p>
            </span>
          )}
          {showBrand && <p className={styles.brand}>{brandTitle}</p>}
        </div>
      )}
    </Link>
  )
}, (prev, next) => (
  prev.className === next.className
  && prev.enableHover === next.enableHover
  && prev.loadingBuyButton === next.loadingBuyButton
  && prev.disableBuyButton === next.disableBuyButton
  && prev.isOutOfStock === next.isOutOfStock
  && prev.onBuy === next.onBuy
  && prev.action === next.action
))
